import type { OrgOverviewByOrganizationQuery } from "gql/generated";

export type OrgOverviewRow = NonNullable<
  NonNullable<
    OrgOverviewByOrganizationQuery["orgOverviewByOrganization"]
  >["rows"]
>[number];

export const OrgOverviewTab = {
  Listings: "LISTINGS",
  People: "PEOPLE",
  Events: "EVENTS",
} as const;
export type OrgOverviewTab = (typeof OrgOverviewTab)[keyof typeof OrgOverviewTab];

/** Mirrors the backend `OrgOverviewEntityType` enum (plain string field on the row type). */
export const OrgOverviewEntityType = {
  Listing: "LISTING",
  Person: "PERSON",
  Event: "EVENT",
} as const;

export type OrgOverviewSortDirection = "asc" | "desc";

export type OrgOverviewColumnKey =
  | "status"
  | "name"
  | "peopleCount"
  | "paidInvoices"
  | "unpaidInvoices"
  | "membershipTierName"
  | "membershipStatusNames"
  | "membershipRenewalDate"
  | "noteCount"
  | "eventCount"
  | "eventStartDate"
  | "eventEndDate"
  | "recurrenceType"
  | "invoices"
  | "attendeeCount"
  | "customTagNames"
  | "primaryTag";

export type OrgOverviewColumn = {
  key: OrgOverviewColumnKey;
  labelKey: string;
  /** Initial pixel width. Live width is tracked via a `--col-{key}` CSS variable so drag-resizing never triggers a React re-render. */
  defaultWidth: number;
  minWidth?: number;
  align?: "left" | "right";
  sortable: boolean;
};

export const DEFAULT_MIN_COLUMN_WIDTH = 60;

/** Column defs must stay in sync between the header row and body rows (enforced via shared `columns` prop). */
export const LISTING_COLUMNS: OrgOverviewColumn[] = [
  { key: "status", labelKey: "STATUS", defaultWidth: 84, minWidth: 72, sortable: true },
  {
    key: "name",
    labelKey: "LISTING_NAME",
    defaultWidth: 280,
    minWidth: 160,
    sortable: true,
  },
  {
    key: "peopleCount",
    labelKey: "PEOPLE",
    defaultWidth: 260,
    minWidth: 140,
    sortable: true,
  },
  {
    key: "paidInvoices",
    labelKey: "PAID_INVOICES",
    defaultWidth: 170,
    sortable: true,
  },
  {
    key: "unpaidInvoices",
    labelKey: "UNPAID_INVOICES",
    defaultWidth: 170,
    sortable: true,
  },
  {
    key: "membershipTierName",
    labelKey: "MEMBERSHIP_TIER",
    defaultWidth: 150,
    sortable: true,
  },
  {
    key: "membershipStatusNames",
    labelKey: "MEMBERSHIP_STATUS",
    defaultWidth: 150,
    sortable: true,
  },
  {
    key: "membershipRenewalDate",
    labelKey: "MEMBERSHIP_RENEWAL_DATE",
    defaultWidth: 160,
    sortable: true,
  },
  {
    key: "noteCount",
    labelKey: "NOTES",
    defaultWidth: 80,
    sortable: true,
  },
  {
    key: "eventCount",
    labelKey: "EVENTS",
    defaultWidth: 80,
    sortable: true,
  },
  {
    key: "customTagNames",
    labelKey: "TAGS",
    defaultWidth: 220,
    minWidth: 140,
    sortable: false,
  },
  {
    key: "primaryTag",
    labelKey: "PRIMARY_TAG",
    defaultWidth: 160,
    sortable: true,
  },
];

export const PEOPLE_COLUMNS: OrgOverviewColumn[] = [
  {
    key: "name",
    labelKey: "NAME",
    defaultWidth: 240,
    minWidth: 160,
    sortable: true,
  },
  {
    key: "paidInvoices",
    labelKey: "PAID_INVOICES",
    defaultWidth: 170,
    sortable: true,
  },
  {
    key: "unpaidInvoices",
    labelKey: "UNPAID_INVOICES",
    defaultWidth: 170,
    sortable: true,
  },
  {
    key: "membershipTierName",
    labelKey: "MEMBERSHIP_TIER",
    defaultWidth: 150,
    sortable: true,
  },
  {
    key: "membershipStatusNames",
    labelKey: "MEMBERSHIP_STATUS",
    defaultWidth: 150,
    sortable: true,
  },
  {
    key: "membershipRenewalDate",
    labelKey: "MEMBERSHIP_RENEWAL_DATE",
    defaultWidth: 160,
    sortable: true,
  },
  {
    key: "noteCount",
    labelKey: "NOTES",
    defaultWidth: 80,
    sortable: true,
  },
  {
    key: "customTagNames",
    labelKey: "TAGS",
    defaultWidth: 240,
    minWidth: 140,
    sortable: false,
  },
];

export const EVENTS_COLUMNS: OrgOverviewColumn[] = [
  {
    key: "name",
    labelKey: "EVENT_NAME",
    defaultWidth: 280,
    minWidth: 160,
    sortable: true,
  },
  {
    key: "eventStartDate",
    labelKey: "START_DATE",
    defaultWidth: 130,
    sortable: true,
  },
  {
    key: "eventEndDate",
    labelKey: "END_DATE",
    defaultWidth: 130,
    sortable: true,
  },
  {
    key: "recurrenceType",
    labelKey: "RECURRENCE_TYPE",
    defaultWidth: 140,
    sortable: true,
  },
  {
    key: "invoices",
    labelKey: "INVOICES",
    defaultWidth: 150,
    sortable: true,
  },
  {
    key: "attendeeCount",
    labelKey: "ATTENDEES",
    defaultWidth: 120,
    sortable: true,
  },
  {
    key: "customTagNames",
    labelKey: "TAGS",
    defaultWidth: 220,
    minWidth: 140,
    sortable: false,
  },
];

export function cssWidthVar(key: OrgOverviewColumnKey): string {
  return `--col-${key}`;
}

/** Grid template built from CSS variables so a drag-resize only needs to mutate the variable on a shared ancestor. */
export function getGridTemplateColumns(columns: OrgOverviewColumn[]): string {
  return columns.map(col => `var(${cssWidthVar(col.key)})`).join(" ");
}

/** Seeds the `--col-{key}` CSS variables with their default pixel widths for a set of column lists. */
export function buildDefaultColumnWidthVars(
  columnLists: OrgOverviewColumn[][],
): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const columns of columnLists) {
    for (const column of columns) {
      const varName = cssWidthVar(column.key);
      if (!(varName in vars)) {
        vars[varName] = `${column.defaultWidth}px`;
      }
    }
  }
  return vars;
}

/** Returns the raw comparable value for a column, used by the sort comparator. */
export function getSortValue(
  row: OrgOverviewRow,
  key: OrgOverviewColumnKey,
): string | number {
  switch (key) {
    case "status":
      return row.status ?? "";
    case "name":
      return row.name?.toLowerCase() ?? "";
    case "peopleCount":
      return row.peopleCount ?? 0;
    case "paidInvoices":
      return row.paidInvoiceTotal ?? 0;
    case "unpaidInvoices":
      return row.unpaidInvoiceTotal ?? 0;
    case "membershipTierName":
      return row.membershipTierName?.toLowerCase() ?? "";
    case "membershipStatusNames":
      return row.membershipStatusNames?.join(", ").toLowerCase() ?? "";
    case "membershipRenewalDate":
      return row.membershipRenewalDate
        ? new Date(row.membershipRenewalDate).getTime()
        : 0;
    case "noteCount":
      return row.noteCount ?? 0;
    case "eventCount":
      return row.eventCount ?? 0;
    case "eventStartDate":
      return row.eventStartDate ?? "";
    case "eventEndDate":
      return row.eventEndDate ?? "";
    case "recurrenceType":
      return row.recurrenceType?.toLowerCase() ?? "";
    case "invoices":
      return (row.paidInvoiceTotal ?? 0) + (row.unpaidInvoiceTotal ?? 0);
    case "attendeeCount":
      return row.attendeeCount ?? 0;
    case "primaryTag":
      return row.primaryTag?.toLowerCase() ?? "";
    case "customTagNames":
      return row.customTagNames?.join(", ").toLowerCase() ?? "";
    default:
      return "";
  }
}
